import { makeStyles, tokens } from '@coreai/fluentui-react';
import { ottoTokens } from '@/theme/ottoTokens';

export const useFiltersPanelStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },

  // Collapsible header
  header: {
    appearance: 'none',
    border: 'none',
    width: '100%',
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    userSelect: 'none',
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  headerIcon: {
    color: tokens.colorNeutralForeground3,
  },
  headerBadge: {
    marginLeft: tokens.spacingHorizontalXS,
  },
  headerChevron: {
    color: tokens.colorNeutralForeground3,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },

  // Expanded body
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    animationName: {
      '0%': { opacity: 0, transform: `translateY(calc(-1 * ${tokens.spacingVerticalS}))` },
      '100%': { opacity: 1, transform: 'translateY(0)' },
    },
    animationDuration: tokens.durationFast,
    animationTimingFunction: tokens.curveDecelerateMax,
    animationFillMode: 'forwards',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      animationDuration: '0.01ms',
    },
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  groupLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },

  // Search
  searchInput: {
    width: '100%',
  },

  // Status checkbox list
  statusOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  statusOption: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transitionProperty: 'background-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: `translateX(${tokens.spacingHorizontalXXS})`,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':hover': {
        transform: 'none',
      },
    },
  },
  statusOptionSelected: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  statusCheckbox: {
    width: tokens.spacingHorizontalL,
    height: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  statusCheckboxChecked: {
    backgroundColor: tokens.colorBrandBackground,
    border: `1px solid ${tokens.colorBrandBackground}`,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  statusDot: {
    width: tokens.spacingHorizontalS,
    height: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
  statusDotSucceeded: { backgroundColor: ottoTokens.statusSucceeded },
  statusDotFailed: { backgroundColor: ottoTokens.statusFailed },
  statusDotRunning: { backgroundColor: ottoTokens.statusRunning },
  statusDotCancelled: { backgroundColor: ottoTokens.statusCancelled },
  statusDotSkipped: { backgroundColor: ottoTokens.statusSkipped },
  statusText: {
    flex: 1,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },

  // Segmented control (version / time range)
  segmentedControl: {
    display: 'flex',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalXXS,
    gap: tokens.spacingHorizontalXXS,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  segment: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    cursor: 'pointer',
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground3,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  segmentActive: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },

  clearAll: {
    alignSelf: 'stretch',
    justifyContent: 'center',
  },
});
